import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import usePatchNote from '../../../API/hooks/notes/usePatchNote';
import { PatchNoteRequest } from '../../../API/types/Notes';
import Input from '../../common/Input/Input';
import { Button } from '../../ui/Button/Button';
import S from './EditNoteStyles';
import { NoteTitles } from '../../../API/services/NotesService';

type Props = {
  noteId: string;
  fleetId: string;
  entityId: string;
  entityName: string;
  title?: string;
  text?: string;
  toggleModal: (closed: boolean) => void;
};

function EditNote({
  noteId, fleetId, entityId, entityName, title, text, toggleModal,
}: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  // formik can't process async actions and work with server response,
  // thus we have to pass callbacks to tanstack mutation
  const { mutate } = usePatchNote(onSubmitSuccess, onSubmitFail);

  const formik = useFormik({
    initialValues: {
      id: noteId,
      fleetId,
      entityId,
      entityName,
      title: title || NoteTitles[entityName],
      text: text || '',
    },
    onSubmit: (values) => mutate({ ...values } as unknown as PatchNoteRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.text.length) errors.firstName = 'Note can\'t be empty';
      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="details-container">
          <S.RowContainer>
            <Input
              label="Title"
              name="title"
              placeholder="Type a title for note (optional)"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.title}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Note"
              name="text"
              placeholder="Describe your point here"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.text}
              error={formik.touched.text && formik.errors.text}
            />
          </S.RowContainer>
          <S.RowContainer>
            <div className="actions">
              <Button
                height="50px"
                onClick={() => toggleModal(false)}
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="save-button"
                height="50px"
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid || !!submitError}
              >
                Save
              </Button>
            </div>
          </S.RowContainer>

          {!!submitError && (
            typeof submitError === 'string'
              ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
              : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
          )}
        </div>
      </form>
    </S.Wrapper>
  );
}

export default EditNote;
